/**
 * Stale-chunk recovery helpers.
 *
 * Quando um novo deploy de main acontece, os usuários que estavam logados
 * têm a versão antiga do app em memória. Ao tentar carregar um chunk lazy
 * (ex.: abrir uma página que ainda não tinha sido visitada), o navegador
 * recebe 404 / Failed to fetch pra um arquivo cujo nome com hash já não
 * existe mais — porque foi substituído por outro hash.
 *
 * Este módulo centraliza a detecção desse cenário e o auto-reload, usado
 * tanto pelo handler global do vite:preloadError (main.tsx) quanto pelo
 * ErrorBoundary (rede de segurança).
 *
 * Anti-loop: marcamos timestamp da última tentativa em sessionStorage. Se
 * o erro persistir após reload (provavelmente NÃO é deploy novo — algum
 * problema real de CDN ou bug), paramos de recarregar e mostramos o erro
 * de verdade ao usuário.
 */

const STALE_CHUNK_PATTERNS: RegExp[] = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
  /Loading chunk \d+ failed/i,
  /Loading CSS chunk/i,
  /ChunkLoadError/i,
  /dynamically imported module/i,
  // Quando o SPA fallback ja esta CACHEADO no lugar do .js, o navegador nem
  // chega a falhar o fetch: ele baixa o index.html com HTTP 200 e recusa na
  // checagem de MIME. A mensagem entao NAO cita "dynamically imported module"
  // — cada navegador escreve do seu jeito, e nenhum dos dois casava acima.
  // Medido em producao (04/08 a 18/08): 3 quedas, 2 pessoas, todas na
  // redacao do WebKit, todas sem auto-recuperacao — tela de erro na cara de
  // quem nao podia fazer nada. As outras 14 do mesmo periodo se curaram.
  /is not a valid JavaScript MIME type/i,          // WebKit / Safari
  /Expected a JavaScript(-or-Wasm)? module script/i, // Chromium
]

const AUTO_RELOAD_KEY = "creditos:autoReloadAt"
const AUTO_RELOAD_COOLDOWN_MS = 30_000

export function isStaleChunkError(error: unknown): boolean {
  if (!error) return false
  const err = error as { message?: string; name?: string }
  const msg = err.message ?? String(error)
  const name = err.name ?? ""
  return STALE_CHUNK_PATTERNS.some((p) => p.test(msg) || p.test(name))
}

export function recentlyAutoReloaded(): boolean {
  try {
    const last = sessionStorage.getItem(AUTO_RELOAD_KEY)
    if (!last) return false
    const ts = Number(last)
    if (!Number.isFinite(ts)) return false
    return Date.now() - ts < AUTO_RELOAD_COOLDOWN_MS
  } catch {
    return false
  }
}

function markAutoReload(): void {
  try {
    sessionStorage.setItem(AUTO_RELOAD_KEY, String(Date.now()))
  } catch {
    /* sessionStorage pode estar bloqueado (modo anônimo); reload mesmo assim */
  }
}

/** Extrai a URL do chunk (.js/.mjs/.css) da mensagem de erro, quando presente. */
export function extractChunkUrl(error: unknown): string | null {
  if (!error) return null
  const err = error as { message?: string }
  const msg = err.message ?? String(error)
  const match = msg.match(/https?:\/\/[^\s"')]+?\.(?:js|mjs|css)/i)
  return match ? match[0] : null
}

const REPAIR_TIMEOUT_MS = 2_000

/**
 * Repara entradas de cache envenenadas antes do reload.
 *
 * Cenário real (2026-07-27): durante a janela de um deploy, o navegador pede um
 * chunk que ainda não foi publicado. O Cloudflare Pages responde com o SPA
 * fallback — `index.html`, HTTP **200**, `text/html` — e o navegador guarda
 * ESSE HTML no lugar do `.js`. Como os assets têm `Cache-Control: max-age=14400`,
 * a entrada errada persiste por horas e o `import()` falha por strict MIME
 * checking ("Expected a JavaScript-or-Wasm module script but the server
 * responded with a MIME type of text/html").
 *
 * `window.location.reload()` NÃO resolve: recarrega o documento, mas a entrada
 * envenenada continua no HTTP cache — daí o loop de "Atualizar a página".
 * `caches.delete()` também não basta: mexe só no Cache Storage (service worker),
 * não no HTTP cache.
 *
 * O que de fato corrige é `fetch(url, { cache: "reload" })`, que força ida à
 * rede E **sobrescreve** a entrada no HTTP cache. Reparamos o chunk que falhou,
 * o documento e os assets do `<head>`, com timeout curto pra nunca travar o
 * reload — se o reparo falhar, recarrega mesmo assim (o loop-guard segura).
 */
async function repairPoisonedCache(chunkUrl: string | null): Promise<void> {
  const urls = new Set<string>()
  if (chunkUrl) urls.add(chunkUrl)

  try {
    urls.add(new URL("/", window.location.origin).href)
    document
      .querySelectorAll<HTMLScriptElement>('script[type="module"][src]')
      .forEach((el) => el.src && urls.add(el.src))
    document
      .querySelectorAll<HTMLLinkElement>('link[rel="modulepreload"][href]')
      .forEach((el) => el.href && urls.add(el.href))
  } catch {
    /* DOM indisponível: segue com o que já foi coletado */
  }

  const sameOrigin = [...urls].filter((u) => {
    try {
      return new URL(u).origin === window.location.origin
    } catch {
      return false
    }
  })

  const repair = Promise.allSettled(
    sameOrigin.map((u) => fetch(u, { cache: "reload", credentials: "same-origin" })),
  )
  const timeout = new Promise<void>((resolve) =>
    window.setTimeout(resolve, REPAIR_TIMEOUT_MS),
  )
  await Promise.race([repair, timeout])

  // Resquício de PWA removido: limpa Cache Storage por garantia.
  try {
    if ("caches" in window) {
      const keys = await caches.keys()
      await Promise.allSettled(keys.map((k) => caches.delete(k)))
    }
  } catch {
    /* best-effort */
  }
}

/**
 * Agenda reload silencioso da página em `delayMs` (default 1500ms).
 * Marca timestamp em sessionStorage pra evitar loops infinitos.
 *
 * Passe o `error` original quando disponível: a URL do chunk é extraída dele
 * e a entrada de cache correspondente é reparada antes do reload (ver
 * `repairPoisonedCache`). Sem o `error`, o reparo cobre documento + assets do
 * `<head>`, que já resolve a maioria dos casos.
 */
export function scheduleAutoReload(delayMs = 1500, error?: unknown): void {
  markAutoReload()
  const chunkUrl = extractChunkUrl(error)
  window.setTimeout(() => {
    void repairPoisonedCache(chunkUrl).finally(() => window.location.reload())
  }, delayMs)
}
